import { inject, Injectable } from '@angular/core'
import { ShopifyService } from '../shopify.service'
import { UserService } from '../user.service'
import { filter, forkJoin, Subscription, take, switchMap, debounceTime, Subject, BehaviorSubject, tap, pipe} from 'rxjs'
import { EnforcementWlService } from '../../../pages/admin-panel/enforcement-control/services/enforcement-wl.service'
import { EnforcementWhiteList, enforcementWLMappings } from '../../../pages/admin-panel/enforcement-control/models/enforcement-control.model'
import { PlansV3Identifier } from '../../../shared/models/subscription-plan.model'
import { LimitBlockerService } from './limit-blocker.service'
import { PlanSelectedGuard } from '../../guards/plan-selected.guard'
import { isIgnoredAccount } from './ignored-accounts'
import { SlackChannelName, SlackSupporService } from '../slack-support.service'
import { SafeLocalStorageService } from '../safe-local-storage.service'
import { AdminUserTokenService } from '../admin-user-token.service'

@Injectable({providedIn: 'root'})
export class ShopifyLimitEnforcementService {
  private subscription: Subscription = new Subscription()
  public markAsRead: boolean = false
  public enforced$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private skipPages = [
    'onboarding',
    'settings/billing',
  ]
  private forbiddenShopifyPlans = inject(PlanSelectedGuard).hideStripePlansFrom
  private initSubject = new Subject<void>()

  constructor(
    private enforcementWlService: EnforcementWlService,
    private limitBlockerService: LimitBlockerService,
    private shopifyService: ShopifyService,
    private userService: UserService,
    private slackSupporService: SlackSupporService,
    private safeLocalStorageService: SafeLocalStorageService,
    private adminUserTokenService: AdminUserTokenService,
  ) {
    this.subscription.add(
      this.userService.userInfo$.asObservable().pipe(
        filter(userInfo => userInfo !== null),
        take(1),
        tap((_userInfo) => {
          if (!isIgnoredAccount(_userInfo.id)) {
            this.runChecks()
          }
        })
      ).subscribe()
    )
  }

  runChecks() {
    this.subscription.add(
      this.initSubject.pipe(
        debounceTime(500), // Adjust the debounce time as needed
        switchMap(() => forkJoin([
          this.userService.readInteractions(),
          this.shopifyService.activeKnownCustomersCount$.pipe(
            filter(akcs => akcs !== null),
            take(1)
          )
        ]))
      ).subscribe(([interactions, factualAkc]) => {
        const currentShopifyPlan = this.userService.shopifyPlanName
        const planSubscription = this.userService.userInfo?.subscription
        const isBudding = this.userService.isLegacyBuddingPlanUser
        const isFreeV3Plan = this.userService.isNewFreeV3PlanUser
        const subscribedToAkc = isBudding || isFreeV3Plan ? planSubscription?.max_active_contacts : planSubscription?.usage_plan?.max
        const isShopifyShop = this.userService.isShopifyShop
        const notStripePlan = ![PlansV3Identifier.NewCustomFree, PlansV3Identifier.NewCustomPaid].includes(planSubscription?.plan?.payment_gateway_plan_identifier as any)
        const isWhitelisted = this.isWhitelisted(interactions.e as EnforcementWhiteList)
        const akcLeeway = 500
        const exceedsLimit = factualAkc > subscribedToAkc
        const exceedsLeewayLimit = factualAkc > (subscribedToAkc + akcLeeway)
        const currentPlanNotForbidden = !this.forbiddenShopifyPlans.includes(currentShopifyPlan)
        // const contactsBasedPlan = planSubscription?.plan?.anchor_metric === 'contacts'
        const isv3BasicPlan = planSubscription?.plan?.payment_gateway_plan_identifier === PlansV3Identifier.Basic
        const isv3StarterPlan = planSubscription?.plan?.payment_gateway_plan_identifier === PlansV3Identifier.Starter
        if ((isBudding && !isWhitelisted) || (
            currentPlanNotForbidden &&
            isFreeV3Plan &&
            isShopifyShop &&
            notStripePlan &&
            !isWhitelisted
        )) {
          const silent = this.skipPages.some((page) => window.location.pathname.includes(page))
          this.limitBlockerService.deactivateAll({
            factualAkc,
            subscribedToAkc,
            akcExceedsLimit: exceedsLimit,
            silent: silent || !this.shouldShowDailyPopup(),
          })
          if (this.shouldShowDailyPopup()) {
            this.setDailyPopupShown()
          }
          this.enforced$.next(true)
        } else if (
          (isv3BasicPlan || isv3StarterPlan) &&
          factualAkc < 5000 &&
          exceedsLeewayLimit &&
          isShopifyShop &&
          notStripePlan &&
          !isWhitelisted
        ) {
          // Using softer enforcement for v3 basic and starter plans since they are on a payed plan
          this.limitBlockerService.showUpgradePopup({
            factualAkc,
            subscribedToAkc,
            akcExceedsLimit: exceedsLimit,
            silent: !this.shouldShowDailyPopup(),
            akcLeewayGiven: true,
          }, true)
          if (this.shouldShowDailyPopup()) {
            this.setDailyPopupShown()
            // Only send slack message if not logged in as admin
            if (this.adminUserTokenService.getValue() === null) {
              this.slackSupporService.sendMessage([`${subscribedToAkc}`, `${factualAkc}`], SlackChannelName.AkcLimitEnforcement)
            }
          }
        } else {
          this.enforced$.next(false)
        }
      })
    )
  }
  
  init() {
    this.initSubject.next()
  }

  public isWhitelisted(whiteList: EnforcementWhiteList): boolean {
    const wlKey = enforcementWLMappings['akc']
    let isWhitelisted = whiteList?.[wlKey]?.e
    const whiteListTTL = whiteList?.[wlKey]?.t
    if (whiteListTTL && whiteListTTL < Date.now()) {
      isWhitelisted = false
    }
    return isWhitelisted
  }

  private shouldShowDailyPopup(): boolean {
    const userId = this.userService.userInfo?.id
    if (!userId) return false
    
    const storageKey = `shopify-v3-popup-last-shown-${userId}`
    const lastShown = this.safeLocalStorageService.getItem(storageKey)
    
    if (!lastShown) return true
    
    const lastShownTime = parseInt(lastShown, 10)
    const oneDayInMs = 24 * 60 * 60 * 1000
    const now = Date.now()
    
    return (now - lastShownTime) >= oneDayInMs
  }

  private setDailyPopupShown(): void {
    const userId = this.userService.userInfo?.id
    if (!userId) return
    
    const storageKey = `shopify-v3-popup-last-shown-${userId}`
    this.safeLocalStorageService.setItem(storageKey, Date.now().toString())
  }

}
